import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import { storableError } from '../util/errors';
import { transitionsToRequested } from '../util/transaction';
import { ENGAGEMENT_LISTING, LISTING_STATE_DRAFT, LISTING_STATE_PUBLISHED } from '../util/types';
import * as log from '../util/log';
import { authInfo } from './Auth.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import { util as sdkUtil } from '../util/sdkLoader';
import { BUYER_ROLE, COACHEE_ROLE } from '../marketplace-custom-config';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

export const FETCH_ACCESSIBLE_COACH_LIST_REQUEST = 'app/user/FETCH_ACCESSIBLE_COACH_LIST_REQUEST';
export const FETCH_ACCESSIBLE_COACH_LIST_SUCCESS = 'app/user/FETCH_ACCESSIBLE_COACH_LIST_SUCCESS';
export const FETCH_ACCESSIBLE_COACH_LIST_ERROR = 'app/user/FETCH_ACCESSIBLE_COACH_LIST_ERROR';

export const FETCH_PROJECT_LISTINGS_REQUEST = 'app/user/FETCH_PROJECT_LISTINGS_REQUEST';
export const FETCH_PROJECT_LISTINGS_SUCCESS = 'app/user/FETCH_PROJECT_LISTINGS_SUCCESS';
export const FETCH_PROJECT_LISTINGS_ERROR = 'app/user/FETCH_PROJECT_LISTINGS_ERROR';

export const HIDE_NEW_AGREEMENT_POPUP = 'app/user/HIDE_NEW_AGREEMENT_POPUP';
export const HIDE_PROFILE_UPDATE_POPUP = 'app/user/HIDE_PROFILE_UPDATE_POPUP';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
  currentUserListing: null,
  currentUserListingFetched: false,
  currentUserFetched: false,
  projectListings: [],
  fetchProjectListingsInProgress: false,
  fetchProjectListingsError: null,
  newAgreementPopupClosed: false,
  profileUpdatePopupClosed: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return {
        ...state,
        currentUser: mergeCurrentUser(state.currentUser, payload),
        currentUserFetched: true,
      };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
        currentUserListing: null,
        currentUserListingFetched: false,
        currentUserFetched: true,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return {
        ...state,
        currentUserHasListings: payload.hasListings,
        currentUserListing: payload.listing,
        currentUserListingFetched: true,
      };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      return { ...state, currentUserNotificationCount: payload.transactions.length };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    case FETCH_ACCESSIBLE_COACH_LIST_REQUEST:
      return { ...state, fetchAccessibleCoachListInProgress: true };
    case FETCH_ACCESSIBLE_COACH_LIST_SUCCESS:
      return {
        ...state,
        fetchAccessibleCoachListInProgress: false,
        accessibleCoachList: payload.accessibleCoachList,
        accessibleEngagementList: payload.accessibleEngagementList,
      };
    case FETCH_ACCESSIBLE_COACH_LIST_ERROR:
      return {
        ...state,
        fetchAccessibleCoachListError: payload,
        fetchAccessibleCoachListInProgress: false,
      };

    case FETCH_PROJECT_LISTINGS_REQUEST:
      return {
        ...state,
        fetchProjectListingsInProgress: true,
        fetchProjectListingsError: null,
      }
    case FETCH_PROJECT_LISTINGS_SUCCESS:
      return { ...state, projectListings: payload, fetchProjectListingsInProgress: false };
    case FETCH_PROJECT_LISTINGS_ERROR:
      return {
        ...state,
        fetchProjectListingsInProgress: false,
        fetchProjectListingsError: payload,
      };
    
    case HIDE_NEW_AGREEMENT_POPUP:
      return { ...state, newAgreementPopupClosed: true };

    case HIDE_PROFILE_UPDATE_POPUP:
      return { ...state, profileUpdatePopupClosed: true };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = (hasListings, listing) => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings, listing },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = transactions => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { transactions },
});

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

export const fetchAccessibleCoachListRequest = () => ({
  type: FETCH_ACCESSIBLE_COACH_LIST_REQUEST,
});

export const fetchAccessibleCoachListSuccess = (accessibleCoachList, accessibleEngagementList) => ({
  type: FETCH_ACCESSIBLE_COACH_LIST_SUCCESS,
  payload: { accessibleCoachList, accessibleEngagementList },
});

export const fetchAccessibleCoachListError = e => ({
  type: FETCH_ACCESSIBLE_COACH_LIST_ERROR,
  error: true,
  payload: e,
});

export const fetchProjectListingsRequest = () => ({
  type: FETCH_PROJECT_LISTINGS_REQUEST
});

export const fetchProjectListingsSuccess = projectListings => ({
  type: FETCH_PROJECT_LISTINGS_SUCCESS,
  payload: projectListings,
});

export const fetchProjectListingsError = e => ({
  type: FETCH_PROJECT_LISTINGS_ERROR,
  payload: e,
})

export const hideNewAgreementPopup = () => ({
  type: HIDE_NEW_AGREEMENT_POPUP,
});

export const hideProfileUpdatePopup = () => ({
  type: HIDE_PROFILE_UPDATE_POPUP,
});

// ================ Thunks ================ //

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has
    // listings, we only need at most one result.
    page: 1,
    per_page: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;
      const listing = hasListings ? response.data.data[0] : null;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings, listing));
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    per_page: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserNotificationsRequest());

  const apiQueryParams = {
    only: 'sale',
    last_transitions: transitionsToRequested,
    page: 1,
    per_page: NOTIFICATION_PAGE_SIZE,
  };

  sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      const transactions = response.data.data;
      dispatch(fetchCurrentUserNotificationsSuccess(transactions));
    })
    .catch(e => dispatch(fetchCurrentUserNotificationsError(storableError(e))));
};

export const fetchAccessibleCoachList = () => (dispatch, getState, sdk) => {
  dispatch(fetchAccessibleCoachListRequest());
  const currentUser = getState().user.currentUser;

  if (!currentUser) {
    dispatch(fetchAccessibleCoachListSuccess([]));
    return Promise.resolve(null);
  }

  return sdk.listings
    .query({ meta_coacheeId: currentUser.id.uuid })
    .then(response => {
      const accessibleCoachList = response.data.data.reduce((acc, engagement) => {
        const { coachList = {} } = engagement.attributes.metadata || {};
        const coachListingIds = Object.keys(coachList);
        return [...acc, ...coachListingIds];
      }, []);

      const accessibleEngagementList = response.data.data.map(engagement => engagement.id.uuid);

      dispatch(fetchAccessibleCoachListSuccess(accessibleCoachList, accessibleEngagementList));
    })
    .catch(e => dispatch(fetchAccessibleCoachListError(storableError(e))));
};

export const fetchCurrentUser = (params = null) => (dispatch, getState, sdk) => {
  dispatch(currentUserShowRequest());
  const { isAuthenticated } = getState().Auth;

  if (!isAuthenticated) {
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));
    return Promise.resolve({});
  }

  const parameters = params || {
    include: ['profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));
      return currentUser;
    })
    .then(currentUser => {
      dispatch(fetchCurrentUserHasListings());
      dispatch(fetchCurrentUserNotifications());
      if (!currentUser.attributes.emailVerified) {
        dispatch(fetchCurrentUserHasOrders());
      }
      if (currentUser.attributes.profile.publicData.role === COACHEE_ROLE) {
        dispatch(fetchAccessibleCoachList());
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};

export const fetchProjectListingsOfBuyer = userId => (dispatch, getState, sdk) => {
  dispatch(fetchProjectListingsRequest());
  return sdk.ownListings
    .query()
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      dispatch(fetchProjectListingsSuccess(entities));
    })
    .catch(error => dispatch(fetchProjectListingsError(storableError(error))));
};

export const fetchProjectListingsOfCoachee = email => (dispatch, getState, sdk) => {
  dispatch(fetchProjectListingsRequest());
  return sdk.listings
    .query({
      state: LISTING_STATE_PUBLISHED,
      pub_listingType: ENGAGEMENT_LISTING,
      pub_emailCoachee: email,
    })
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      dispatch(fetchProjectListingsSuccess(entities));
    })
    .catch(error => dispatch(fetchProjectListingsError(storableError(error))));
};

export const fetchProjectListings = () => async (dispatch, getState, sdk) => {
  let currentUser = getState().user.currentUser;
  let { isAuthenticated } = getState().Auth;

  if (!isAuthenticated) {
    return Promise.resolve({});
  }

  if (!currentUser) {
    await dispatch(fetchCurrentUser()).then(() => {
      currentUser = getState().user.currentUser;
    });
  }

  const { role } = currentUser.attributes.profile.publicData || {};

  if (role === BUYER_ROLE) {
    dispatch(fetchProjectListingsOfBuyer(currentUser.id.uuid));
  } else if (role === COACHEE_ROLE) {
    dispatch(fetchProjectListingsOfCoachee(currentUser.attributes.email));
  }
};
